import React, { useState, useEffect, useCallback, useRef } from 'react';
import '../styles/responsive/pages/Tokens.css';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import {
  Coins,
  Plus,
  Edit2,
  Trash2,
  ToggleLeft,
  ToggleRight,
  AlertCircle,
  X,
  Search,
  RefreshCw,
  Filter,
  ExternalLink,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Copy,
  Check,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { useTheme } from '../context/ThemeContext';
import { fetchWithAuth } from '../utils/api';
import useDebounce from '../hooks/useDebounce';
import { TokenLogo, ChainLogo } from '../components/common/CryptoLogo';
import AssetSelect from '../components/common/AssetSelect';
import Skeleton from '../components/common/Skeleton';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const TokenModal = ({ isOpen, onClose, token, networks, selectedNetworkId, onSave, isLoading }) => {
  const [formData, setFormData] = useState({
    networkId: selectedNetworkId || '',
    name: '',
    symbol: '',
    contractAddress: '',
    decimals: 18,
    logoUrl: '',
    isNative: false,
    isStablecoin: false,
    coingeckoId: '',
    status: 'active',
    displayOrder: 0,
  });
  const [errors, setErrors] = useState({});
  // Auto-fetch (name/symbol/decimals/logo/display order) from the token contract.
  const [resolving, setResolving] = useState(false);
  const [resolveStatus, setResolveStatus] = useState({ type: 'idle', message: '' });
  const resolveKeyRef = useRef('');

  useEffect(() => {
    if (token) {
      setFormData({
        networkId: token.networkId || selectedNetworkId || '',
        name: token.name || '',
        symbol: token.symbol || '',
        contractAddress: token.contractAddress || '',
        decimals: token.decimals || 18,
        logoUrl: token.logoUrl || '',
        isNative: token.isNative || false,
        isStablecoin: token.isStablecoin || false,
        coingeckoId: token.coingeckoId || '',
        status: token.status || 'active',
        displayOrder: token.displayOrder || 0,
      });
    } else {
      setFormData({
        networkId: selectedNetworkId || '',
        name: '',
        symbol: '',
        contractAddress: '',
        decimals: 18,
        logoUrl: '',
        isNative: false,
        isStablecoin: false,
        coingeckoId: '',
        status: 'active',
        displayOrder: 0,
      });
    }
    setErrors({});
    setResolveStatus({ type: 'idle', message: '' });
    resolveKeyRef.current = '';
  }, [token, selectedNetworkId, isOpen]);

  // When adding a non-native token, auto-read its metadata from the contract on
  // the selected network and fill name/symbol/decimals/logo/display order.
  useEffect(() => {
    if (!isOpen || token || formData.isNative) return;
    const addr = formData.contractAddress?.trim();
    const nid = formData.networkId;
    if (!nid || !addr || !/^0x[a-fA-F0-9]{40}$/.test(addr)) return;
    const key = `${nid}:${addr.toLowerCase()}`;
    if (resolveKeyRef.current === key) return;

    let cancelled = false;
    setResolving(true);
    setResolveStatus({ type: 'idle', message: '' });
    const timer = setTimeout(async () => {
      try {
        const resp = await fetchWithAuth(`${API_BASE_URL}/tokens/resolve`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ networkId: nid, contractAddress: addr }),
        });
        const data = await resp.json();
        if (cancelled) return;
        resolveKeyRef.current = key;
        setResolving(false);
        if (resp.ok && data.success) {
          const d = data.data;
          setFormData((prev) => ({
            ...prev,
            name: d.name ?? prev.name,
            symbol: d.symbol ?? prev.symbol,
            decimals: d.decimals ?? prev.decimals,
            logoUrl: d.logoUrl || prev.logoUrl,
            displayOrder: d.displayOrder ?? prev.displayOrder,
          }));
          setErrors((prev) => ({ ...prev, contractAddress: '' }));
          setResolveStatus({ type: 'ok', message: 'Verified ERC-20 — details auto-filled from the contract.' });
        } else {
          setResolveStatus({ type: 'err', message: data.message || 'Could not validate this contract.' });
          setErrors((prev) => ({ ...prev, contractAddress: data.message || 'Invalid token contract' }));
        }
      } catch {
        if (cancelled) return;
        setResolving(false);
        setResolveStatus({ type: 'err', message: 'Could not validate this contract. Check the address and network.' });
      }
    }, 600);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [isOpen, token, formData.isNative, formData.networkId, formData.contractAddress]);

  const validateForm = () => {
    const newErrors = {};
    if (!formData.networkId) newErrors.networkId = 'Network is required';
    if (!formData.name.trim()) newErrors.name = 'Token name is required';
    if (!formData.symbol.trim()) newErrors.symbol = 'Token symbol is required';
    if (!formData.isNative && !formData.contractAddress.trim()) {
      newErrors.contractAddress = 'Contract address is required for non-native tokens';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      const dataToSave = { ...formData };
      if (formData.isNative) {
        dataToSave.contractAddress = null;
      }
      onSave(dataToSave);
    }
  };

  if (!isOpen) return null;

  const activeNetworks = networks.filter((n) => n.status === 'active');

  return (
    <div className="modal-overlay">
      <div className="modal-container">
        <div className="modal-header">
          <h3>{token ? 'Edit Token' : 'Add New Token'}</h3>
          <button className="modal-close" onClick={onClose}>
            <X size={20} />
          </button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div className="form-group">
              <label>Network *</label>
              <AssetSelect
                kind="chain"
                value={formData.networkId}
                onChange={(v) => setFormData({ ...formData, networkId: v })}
                disabled={!!token}
                placeholder="Select a network"
                className={errors.networkId ? 'input-error' : ''}
                options={activeNetworks.map((network) => ({
                  value: network.id,
                  label: `${network.name}${network.isTestnet ? ' (Testnet)' : ''}`,
                  chain: network.slug || network.name,
                  logoUrl: network.logoUrl,
                }))}
              />
              {errors.networkId && <span className="error-text"><AlertCircle size={14} /> {errors.networkId}</span>}
              {activeNetworks.length === 0 && (
                <span className="form-hint warning">No active networks available. Please enable a network first.</span>
              )}
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Token Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., Tether USD"
                  className={errors.name ? 'input-error' : ''}
                />
                {errors.name && <span className="error-text"><AlertCircle size={14} /> {errors.name}</span>}
              </div>
              <div className="form-group">
                <label>Symbol *</label>
                <input
                  type="text"
                  value={formData.symbol}
                  onChange={(e) => setFormData({ ...formData, symbol: e.target.value.toUpperCase() })}
                  placeholder="e.g., USDT"
                  maxLength={20}
                  className={errors.symbol ? 'input-error' : ''}
                />
                {errors.symbol && <span className="error-text"><AlertCircle size={14} /> {errors.symbol}</span>}
              </div>
            </div>

            <div className="form-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={formData.isNative}
                  onChange={(e) => setFormData({ ...formData, isNative: e.target.checked, contractAddress: e.target.checked ? '' : formData.contractAddress })}
                />
                <span>This is a native token (e.g., ETH, MATIC)</span>
              </label>
            </div>

            {!formData.isNative && (
              <div className="form-group">
                <label>Contract Address *</label>
                <input
                  type="text"
                  value={formData.contractAddress}
                  onChange={(e) => {
                    setFormData({ ...formData, contractAddress: e.target.value });
                    setResolveStatus({ type: 'idle', message: '' });
                  }}
                  placeholder="0x..."
                  className={errors.contractAddress ? 'input-error' : ''}
                />
                {errors.contractAddress && <span className="error-text"><AlertCircle size={14} /> {errors.contractAddress}</span>}
                {!token && resolving && (
                  <span className="form-hint"><RefreshCw size={14} /> Reading token details from the contract…</span>
                )}
                {!token && !resolving && resolveStatus.type === 'ok' && (
                  <span className="token-resolved">
                    <TokenLogo symbol={formData.symbol} logoUrl={formData.logoUrl} size={20} />
                    <Check size={14} /> {resolveStatus.message}
                  </span>
                )}
              </div>
            )}

            <div className="form-row">
              <div className="form-group">
                <label>Decimals</label>
                <input
                  type="number"
                  value={formData.decimals}
                  onChange={(e) => setFormData({ ...formData, decimals: parseInt(e.target.value) || 18 })}
                  min={0}
                  max={18}
                />
              </div>
              <div className="form-group">
                <label>Display Order</label>
                <input
                  type="number"
                  value={formData.displayOrder}
                  onChange={(e) => setFormData({ ...formData, displayOrder: parseInt(e.target.value) || 0 })}
                  min={0}
                />
              </div>
            </div>

            <div className="form-group">
              <label>Logo URL</label>
              <input
                type="url"
                value={formData.logoUrl}
                onChange={(e) => setFormData({ ...formData, logoUrl: e.target.value })}
                placeholder="https://example.com/token-logo.png"
              />
            </div>

            <div className="form-group">
              <label>CoinGecko ID (for price data)</label>
              <input
                type="text"
                value={formData.coingeckoId}
                onChange={(e) => setFormData({ ...formData, coingeckoId: e.target.value })}
                placeholder="e.g., tether, usd-coin"
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={formData.isStablecoin}
                    onChange={(e) => setFormData({ ...formData, isStablecoin: e.target.checked })}
                  />
                  <span>This is a stablecoin</span>
                </label>
              </div>
              <div className="form-group">
                <label>Status</label>
                <select
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn-secondary" onClick={onClose} disabled={isLoading}>
              Cancel
            </button>
            <button type="submit" className="btn-primary" disabled={isLoading || activeNetworks.length === 0}>
              {isLoading ? 'Saving...' : (token ? 'Update Token' : 'Add Token')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const ConfirmModal = ({ isOpen, onClose, onConfirm, title, message, isLoading, confirmText = 'Confirm' }) => {
  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-container modal-small">
        <div className="modal-header">
          <h3>{title}</h3>
          <button className="modal-close" onClick={onClose}>
            <X size={20} />
          </button>
        </div>
        <div className="modal-body">
          <p>{message}</p>
        </div>
        <div className="modal-footer">
          <button className="btn-secondary" onClick={onClose} disabled={isLoading}>
            Cancel
          </button>
          <button className="btn-danger" onClick={onConfirm} disabled={isLoading}>
            {isLoading ? 'Processing...' : confirmText}
          </button>
        </div>
      </div>
    </div>
  );
};

const FilterModal = ({ isOpen, onClose, filters, onApply, networks, isSuperAdmin }) => {
  const [localFilters, setLocalFilters] = useState(filters);

  useEffect(() => {
    setLocalFilters(filters);
  }, [filters, isOpen]);

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-container modal-small">
        <div className="modal-header">
          <h3>Filter Tokens</h3>
          <button className="modal-close" onClick={onClose}>
            <X size={20} />
          </button>
        </div>
        <div className="modal-body">
          <div className="form-group">
            <label>Network</label>
            <select
              value={localFilters.networkId}
              onChange={(e) => setLocalFilters({ ...localFilters, networkId: e.target.value })}
            >
              <option value="all">All Networks</option>
              {networks.map((network) => (
                <option key={network.id} value={network.id}>
                  {network.name}
                </option>
              ))}
            </select>
          </div>

          {isSuperAdmin && (
            <div className="form-group">
              <label>Status</label>
              <select
                value={localFilters.status}
                onChange={(e) => setLocalFilters({ ...localFilters, status: e.target.value })}
              >
                <option value="all">All</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
          )}

          <div className="form-group">
            <label>Token Type</label>
            <select
              value={localFilters.isStablecoin}
              onChange={(e) => setLocalFilters({ ...localFilters, isStablecoin: e.target.value })}
            >
              <option value="all">All</option>
              <option value="true">Stablecoin</option>
              <option value="false">Non-Stablecoin</option>
            </select>
          </div>

          <div className="form-group">
            <label>Native Token</label>
            <select
              value={localFilters.isNative}
              onChange={(e) => setLocalFilters({ ...localFilters, isNative: e.target.value })}
            >
              <option value="all">All</option>
              <option value="true">Yes</option>
              <option value="false">No</option>
            </select>
          </div>

          <div className="form-group">
            <label>Sort By</label>
            <select
              value={localFilters.sortBy}
              onChange={(e) => setLocalFilters({ ...localFilters, sortBy: e.target.value })}
            >
              <option value="display_order">Default (chain · native first)</option>
              <option value="name">Name</option>
              <option value="symbol">Symbol</option>
            </select>
          </div>
        </div>
        <div className="modal-footer">
          <button
            className="btn-secondary"
            onClick={() => {
              setLocalFilters({ networkId: 'all', status: 'all', isStablecoin: 'all', isNative: 'all', sortBy: 'display_order' });
            }}
          >
            Reset
          </button>
          <button
            className="btn-primary"
            onClick={() => {
              onApply(localFilters);
              onClose();
            }}
          >
            Apply Filters
          </button>
        </div>
      </div>
    </div>
  );
};

const Tokens = () => {
  const { token: authToken, user } = useSelector((state) => state.auth);
  const { mode } = useTheme();
  const isSuperAdmin = user?.role_id === 1 || user?.roleId === 1;
  const isTestnetMode = mode === 'test';


  const [tokens, setTokens] = useState([]);
  const [networks, setNetworks] = useState([]);
  const [allNetworks, setAllNetworks] = useState([]); 
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 400);
  const [filters, setFilters] = useState({
    networkId: 'all',
    status: 'all',
    isStablecoin: 'all',
    isNative: 'all',
    sortBy: 'display_order',
  });
  const [filterModalOpen, setFilterModalOpen] = useState(false);
  const [tokenModalOpen, setTokenModalOpen] = useState(false);
  const [editingToken, setEditingToken] = useState(null);
  const [confirmModal, setConfirmModal] = useState({ open: false, title: '', message: '', onConfirm: null, confirmText: 'Confirm' });
  const [isSaving, setIsSaving] = useState(false);
  // Server-side pagination: only the current page is loaded (scales to thousands of tokens).
  const PAGE_SIZE = 50;
  const [page, setPage] = useState(1);
  const [pageMeta, setPageMeta] = useState({ total: 0, totalPages: 0, limit: PAGE_SIZE });


  const fetchNetworks = useCallback(async () => {
    try {
      const params = new URLSearchParams({
        isTestnet: isTestnetMode ? 'true' : 'false',
      });
      const endpoint = isSuperAdmin ? '/networks' : '/networks/merchant';
      const response = await fetchWithAuth(`${API_BASE_URL}${endpoint}?${params}`);
      const data = await response.json();
      if (data.success) {
        setNetworks(data.data.networks);
        setAllNetworks(data.data.networks);
      }
    } catch (error) {
      console.error('Failed to fetch networks:', error);
    }
  }, [authToken, isSuperAdmin, isTestnetMode]);

  const fetchTokens = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: String(page),
        limit: String(PAGE_SIZE),
        search: debouncedSearch,
        sortBy: filters.sortBy,
        sortOrder: 'ASC',
      });

      if (filters.networkId !== 'all') params.append('networkId', filters.networkId);
      if (filters.status !== 'all') params.append('status', filters.status);
      if (filters.isStablecoin !== 'all') params.append('isStablecoin', filters.isStablecoin);
      if (filters.isNative !== 'all') params.append('isNative', filters.isNative);
      params.append('isTestnet', isTestnetMode ? 'true' : 'false');

      const endpoint = isSuperAdmin ? '/tokens' : '/tokens/merchant';
      const response = await fetchWithAuth(`${API_BASE_URL}${endpoint}?${params}`);
      const data = await response.json();

      if (data.success) {
        setTokens(data.data.tokens);
        const pg = data.data.pagination || {};
        setPageMeta({
          total: pg.total ?? data.data.tokens.length,
          totalPages: pg.totalPages ?? 1,
          limit: pg.limit ?? PAGE_SIZE,
        });
      } else {
        toast.error(data.message || 'Failed to load tokens');
      }
    } catch (error) {
      toast.error('Failed to load tokens');
    } finally {
      setLoading(false);
    }
  }, [authToken, page, debouncedSearch, filters, isSuperAdmin, isTestnetMode]);

  // Search/filter/mode changes return to page 1 (no-op re-render when already there).
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, filters, isTestnetMode]);

  useEffect(() => {
    fetchNetworks();
  }, [fetchNetworks]);

  useEffect(() => {
    fetchTokens();
  }, [fetchTokens]);

  const getNetworkName = (networkId) => {
    const network = networks.find((n) => n.id === networkId);
    return network?.name || 'Unknown';
  };

  const getNetworkExplorerUrl = (networkId) => {
    const network = networks.find((n) => n.id === networkId);
    return network?.explorerUrl || null;
  };

  // Resolve a network's real logo identity (slug + logoUrl) so the chain can be shown
  // with its actual brand mark via <ChainLogo> instead of a generic globe icon.
  const getNetwork = (networkId) => networks.find((n) => n.id === networkId) || null;

  // Map a network slug/name to its CHAIN color token so the chain name and
  // section header carry the chain's brand color. Falls back to the line color
  // for chains without a dedicated token.
  const chainColor = (chain) => {
    const key = String(chain || '').toLowerCase();
    if (key.includes('eth')) return 'var(--chain-eth)';
    if (key.includes('bnb') || key.includes('binance') || key.includes('bsc')) return 'var(--chain-bnb)';
    if (key.includes('polygon') || key.includes('matic')) return 'var(--chain-polygon)';
    if (key.includes('tron')) return 'var(--chain-tron)';
    if (key.includes('solana') || key.includes('sol')) return 'var(--chain-solana)';
    return 'var(--line-strong)';
  };

  // Copy a contract address to the clipboard, tracking which row was copied.
  const [copiedId, setCopiedId] = useState(null);
  const handleCopyAddress = (token) => {
    if (!token.contractAddress) return;
    navigator.clipboard.writeText(token.contractAddress).then(() => {
      setCopiedId(token.id);
      setTimeout(() => setCopiedId((id) => (id === token.id ? null : id)), 1500);
    });
  };

  const handleSaveToken = async (formData) => {
    setIsSaving(true);
    try {
      const url = editingToken
        ? `${API_BASE_URL}/tokens/${editingToken.id}`
        : `${API_BASE_URL}/tokens`;

      const response = await fetchWithAuth(url, {
        method: editingToken ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();
      if (data.success) {
        toast.success(data.message);
        setTokenModalOpen(false);
        setEditingToken(null);
        fetchTokens();
      } else {
        toast.error(data.message || 'Failed to save token');
      }
    } catch (error) {
      toast.error('Failed to save token');
    } finally {
      setIsSaving(false);
    }
  };

  const handleToggleGlobalStatus = async (token) => {
    try {
      const response = await fetchWithAuth(`${API_BASE_URL}/tokens/${token.id}/toggle-status`, {
        method: 'PATCH',
      });
      const data = await response.json();
      if (data.success) {
        toast.success(data.message);
        fetchTokens();
      } else {
        toast.error(data.message || 'Failed to toggle status');
      }
    } catch (error) {
      toast.error('Failed to toggle status');
    }
  };

  const handleToggleMerchantStatus = async (token) => {
    try {
      const response = await fetchWithAuth(`${API_BASE_URL}/tokens/${token.id}/merchant-toggle`, {
        method: 'PATCH',
      });
      const data = await response.json();
      if (data.success) {
        toast.success(data.message);
        fetchTokens();
      } else {
        toast.error(data.message || 'Failed to toggle token preference');
      }
    } catch (error) {
      toast.error('Failed to toggle token preference');
    }
  };

  const handleDeleteToken = async (token) => {
    setConfirmModal({
      open: true,
      title: 'Delete Token',
      message: `Are you sure you want to delete "${token.name} (${token.symbol})"? This action cannot be undone.`,
      confirmText: 'Delete',
      onConfirm: async () => {
        setIsSaving(true);
        try {
          const response = await fetchWithAuth(`${API_BASE_URL}/tokens/${token.id}`, {
            method: 'DELETE',
          });
          const data = await response.json();
          if (data.success) {
            toast.success(data.message);
            fetchTokens();
          } else {
            toast.error(data.message || 'Failed to delete token');
          }
        } catch (error) {
          toast.error('Failed to delete token');
        } finally {
          setIsSaving(false);
          setConfirmModal({ open: false, title: '', message: '', onConfirm: null });
        }
      },
    });
  };

  // Search and filtering are handled server-side; render the current page directly.
  const rangeStart = pageMeta.total === 0 ? 0 : (page - 1) * pageMeta.limit + 1;
  const rangeEnd = (page - 1) * pageMeta.limit + tokens.length;

  const activeFilterCount = Object.values(filters).filter((v) => v !== 'all' && v !== 'display_order').length;

  return (
    <div className="tokens-page">
      <div className="rd-page-header">
        <div>
          <h1 className="rd-page-title">Networks &amp; tokens</h1>
          <p className="rd-page-subtitle">
            {isSuperAdmin
              ? 'Manage tokens across all networks. Tokens can only be created under active networks.'
              : 'Choose which chains and tokens your customers can pay with.'}
          </p>
        </div>
        {isSuperAdmin && (
          <button
            className="rd-btn rd-btn-primary"
            onClick={() => {
              setEditingToken(null);
              setTokenModalOpen(true);
            }}
            disabled={networks.filter((n) => n.status === 'active').length === 0}
          >
            <Plus size={16} />
            Add token
          </button>
        )}
      </div>

      <div className="rd-tabs nt-tabs">
        <Link to="/dashboard/networks" className="rd-tab">
          Networks
        </Link>
        <Link to="/dashboard/tokens" className="rd-tab active">
          Tokens
          {!loading && <span className="rd-tab-count">{pageMeta.total || tokens.length}</span>}
        </Link>
      </div>

      <div className="search-filter-bar">
        <div className="search-box">
          <Search size={18} />
          <input
            type="text"
            placeholder="Search by name, symbol, or contract address..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <select
          className="inline-select"
          value={filters.networkId}
          onChange={(e) => setFilters({ ...filters, networkId: e.target.value })}
          aria-label="Filter by chain"
        >
          <option value="all">All chains</option>
          {networks.map((n) => (
            <option key={n.id} value={n.id}>{n.name}</option>
          ))}
        </select>
        {isSuperAdmin && (
          <select
            className="inline-select"
            value={filters.status}
            onChange={(e) => setFilters({ ...filters, status: e.target.value })}
            aria-label="Filter by status"
          >
            <option value="all">All statuses</option>
            <option value="active">Active</option>
            <option value="inactive">Disabled</option>
          </select>
        )}
        <button
          className={`btn-filter ${activeFilterCount > 0 ? 'active' : ''}`}
          onClick={() => setFilterModalOpen(true)}
        >
          <Filter size={18} />
          Filters
          {activeFilterCount > 0 && <span className="filter-count">{activeFilterCount}</span>}
        </button>
        <button className="btn-icon-only" onClick={fetchTokens} title="Refresh" aria-label="Refresh tokens">
          <RefreshCw size={18} className={loading ? 'spinning' : ''} />
        </button>
      </div>

      <div className="tokens-table-wrap">
        {loading ? (
          <div className="token-table">
            <div className="ttable-head">
              <span className="th-token">Token</span>
              <span className="th-chain">Chain</span>
              <span className="th-contract">Contract</span>
              <span className="th-dec">Decimals</span>
              <span className="th-status">Status</span>
              <span className="th-actions" />
            </div>
            {Array.from({ length: 2 }, (_, g) => (
              <React.Fragment key={g}>
                <div className="token-group-head">
                  <Skeleton variant="line" width={16} height={16} />
                  <Skeleton variant="line" width={90} height={11} />
                </div>
                {Array.from({ length: 3 }, (_, r) => (
                  <div className="token-row" key={r}>
                    <div className="td-token">
                      <Skeleton variant="card" width={28} height={28} />
                      <div className="token-id">
                        <Skeleton variant="text" width={56} />
                        <Skeleton variant="line" width={88} />
                      </div>
                    </div>
                    <div className="td-chain">
                      <Skeleton variant="line" width={16} height={16} />
                      <Skeleton variant="text" width={70} />
                    </div>
                    <div className="td-contract">
                      <Skeleton variant="text" width={110} />
                    </div>
                    <div className="td-dec">
                      <Skeleton variant="text" width={28} />
                    </div>
                    <div className="td-status">
                      <Skeleton variant="text" width={60} height={20} />
                    </div>
                    <div className="td-actions" />
                  </div>
                ))}
              </React.Fragment>
            ))}
          </div>
        ) : tokens.length === 0 ? (
          <div className="rd-empty nt-state">
            <div className="rd-empty-icon"><Coins size={20} /></div>
            <h3>No tokens found</h3>
            <p>Tokens you add will appear here, ready to enable for checkout.</p>
            {search || activeFilterCount > 0 ? (
              <button
                className="rd-btn rd-btn-secondary nt-empty-btn"
                onClick={() => {
                  setSearch('');
                  setFilters({ networkId: 'all', status: 'all', isStablecoin: 'all', isNative: 'all', sortBy: 'display_order' });
                }}
              >
                Clear search and filters
              </button>
            ) : isSuperAdmin && networks.filter((n) => n.status === 'active').length > 0 ? (
              <button
                className="rd-btn rd-btn-primary nt-empty-btn"
                onClick={() => {
                  setEditingToken(null);
                  setTokenModalOpen(true);
                }}
              >
                <Plus size={16} />
                Add first token
              </button>
            ) : null}
          </div>
        ) : (
          <div className="token-table">
            <div className="ttable-head">
              <span className="th-token">Token</span>
              <span className="th-chain">Chain</span>
              <span className="th-contract">Contract</span>
              <span className="th-dec">Decimals</span>
              <span className="th-status">Status</span>
              <span className="th-actions" />
            </div>
            {tokens.map((token, idx) => {
              const explorer = getNetworkExplorerUrl(token.networkId);
              const merchantDisabled = !isSuperAdmin && token.merchantEnabled === false;
              // Tokens arrive grouped by network (native asset first). Render a light
              // section header whenever the network changes so the order reads clearly.
              const prev = idx > 0 ? tokens[idx - 1] : null;
              const startsGroup = !prev || prev.networkId !== token.networkId;
              const groupName = token.networkName || getNetworkName(token.networkId);
              const groupNet = getNetwork(token.networkId);
              return (
                <React.Fragment key={token.id}>
                  {startsGroup && (
                    <div
                      className="token-group-head"
                      role="rowheader"
                      style={{ '--chain-c': chainColor(groupNet?.slug || groupName) }}
                    >
                      <ChainLogo
                        chain={groupNet?.slug || groupName}
                        logoUrl={groupNet?.logoUrl}
                        size={16}
                      />
                      <span className="tgh-name">{groupName}</span>
                    </div>
                  )}
                <div
                  className={`token-row ${token.status === 'inactive' ? 'is-inactive' : ''}`}
                >
                  <div className="td-token">
                    <TokenLogo symbol={token.symbol} logoUrl={token.logoUrl} size={28} />
                    <div className="token-id">
                      <span className="t-symbol">{token.symbol}</span>
                      <span className="t-name">{token.name}</span>
                    </div>
                  </div>

                  <div className="td-chain">
                    <ChainLogo
                      chain={getNetwork(token.networkId)?.slug || token.networkName || getNetworkName(token.networkId)}
                      logoUrl={getNetwork(token.networkId)?.logoUrl}
                      size={16}
                    />
                    <span>{token.networkName || getNetworkName(token.networkId)}</span>
                  </div>

                  <div className="td-contract">
                    {token.isNative ? (
                      <span className="native-asset">Native asset</span>
                    ) : token.contractAddress ? (
                      <div className="contract-cell">
                        <code>
                          {token.contractAddress.substring(0, 6)}...
                          {token.contractAddress.substring(token.contractAddress.length - 4)}
                        </code>
                        <button
                          className="icon-mini"
                          onClick={() => handleCopyAddress(token)}
                          aria-label="Copy contract address"
                          title="Copy address"
                        >
                          {copiedId === token.id ? <Check size={13} /> : <Copy size={13} />}
                        </button>
                        {explorer && (
                          <a
                            href={`${explorer}/token/${token.contractAddress}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="icon-mini"
                            aria-label="View on block explorer"
                            title="View on explorer"
                          >
                            <ExternalLink size={13} />
                          </a>
                        )}
                      </div>
                    ) : (
                      <span className="muted">—</span>
                    )}
                  </div>

                  <div className="td-dec rd-mono">{token.decimals}</div>

                  <div className="td-status">
                    {isSuperAdmin ? (
                      <span className={`rd-badge ${token.status === 'active' ? 'ok' : 'err'}`}>
                        {token.status === 'active' ? 'Active' : 'Disabled'}
                      </span>
                    ) : (
                      <span className={`rd-badge ${!merchantDisabled ? 'ok' : 'err'}`}>
                        {!merchantDisabled ? 'Enabled' : 'Disabled'}
                      </span>
                    )}
                    {token.isNative && <span className="rd-badge accent plain">Native</span>}
                  </div>

                  <div className="td-actions">
                    {isSuperAdmin ? (
                      <>
                        <button
                          className="row-action"
                          onClick={() => {
                            setEditingToken(token);
                            setTokenModalOpen(true);
                          }}
                          aria-label={`Edit ${token.symbol}`}
                          title="Edit"
                        >
                          <Edit2 size={15} />
                        </button>
                        <button
                          className="row-action"
                          onClick={() => handleToggleGlobalStatus(token)}
                          aria-label={token.status === 'active' ? `Disable ${token.symbol}` : `Enable ${token.symbol}`}
                          title={token.status === 'active' ? 'Disable' : 'Enable'}
                        >
                          {token.status === 'active' ? (
                            <ToggleRight size={18} className="text-success" />
                          ) : (
                            <ToggleLeft size={18} />
                          )}
                        </button>
                        <button
                          className="row-action danger"
                          onClick={() => handleDeleteToken(token)}
                          aria-label={`Delete ${token.symbol}`}
                          title="Delete"
                        >
                          <Trash2 size={15} />
                        </button>
                      </>
                    ) : (
                      <label
                        className="rd-switch"
                        title={!merchantDisabled ? 'Disable' : 'Enable'}
                      >
                        <input
                          type="checkbox"
                          checked={!merchantDisabled}
                          onChange={() => handleToggleMerchantStatus(token)}
                          aria-label={!merchantDisabled ? `Disable ${token.symbol}` : `Enable ${token.symbol}`}
                        />
                        <span className="slider" />
                      </label>
                    )}
                  </div>
                </div>
                </React.Fragment>
              );
            })}
          </div>
        )}
      </div>

      {!loading && tokens.length > 0 && (
        <div className="pagination-bar">
          <span className="pagination-info">
            Showing {rangeStart.toLocaleString('en-US')}&ndash;{rangeEnd.toLocaleString('en-US')} of{' '}
            {pageMeta.total.toLocaleString('en-US')} tokens
          </span>
          {pageMeta.totalPages > 1 && (
            <div className="pagination-controls">
              <button className="page-btn" onClick={() => setPage(1)} disabled={page <= 1} aria-label="First page">
                <ChevronsLeft size={16} />
              </button>
              <button className="page-btn" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1} aria-label="Previous page">
                <ChevronLeft size={16} />
              </button>
              <span className="page-indicator">Page {page} of {pageMeta.totalPages}</span>
              <button className="page-btn" onClick={() => setPage((p) => Math.min(pageMeta.totalPages, p + 1))} disabled={page >= pageMeta.totalPages} aria-label="Next page">
                <ChevronRight size={16} />
              </button>
              <button className="page-btn" onClick={() => setPage(pageMeta.totalPages)} disabled={page >= pageMeta.totalPages} aria-label="Last page">
                <ChevronsRight size={16} />
              </button>
            </div>
          )}
        </div>
      )}

      {!loading && !isSuperAdmin && tokens.length > 0 && (
        <div className="rd-card nt-upsell">
          <div className="nt-upsell-icon">
            <Coins size={20} />
          </div>
          <div className="nt-upsell-copy">
            <h3>Unlock more tokens with Premium</h3>
            <p>Accept additional tokens including memecoins and emerging stablecoins.</p>
          </div>
          <Link to="/dashboard/billing" className="rd-btn rd-btn-purple nt-upsell-btn">
            Upgrade to Premium
          </Link>
        </div>
      )}

      <TokenModal
        isOpen={tokenModalOpen}
        onClose={() => {
          setTokenModalOpen(false);
          setEditingToken(null);
        }}
        token={editingToken}
        networks={networks}
        selectedNetworkId={filters.networkId !== 'all' ? parseInt(filters.networkId) : null}
        onSave={handleSaveToken}
        isLoading={isSaving}
      />

      <FilterModal
        isOpen={filterModalOpen}
        onClose={() => setFilterModalOpen(false)}
        filters={filters}
        networks={networks}
        onApply={setFilters}
        isSuperAdmin={isSuperAdmin}
      />

      <ConfirmModal
        isOpen={confirmModal.open}
        onClose={() => setConfirmModal({ open: false, title: '', message: '', onConfirm: null })}
        onConfirm={confirmModal.onConfirm}
        title={confirmModal.title}
        message={confirmModal.message}
        confirmText={confirmModal.confirmText}
        isLoading={isSaving}
      />
      <style>{`
        .tokens-page { color: var(--ink); }

        .nt-tabs { margin-bottom: 20px; }
        .tokens-page .rd-tab { text-decoration: none; }

        .search-filter-bar {
          display: flex;
          gap: 10px;
          margin-bottom: 20px;
          flex-wrap: wrap;
        }

        .search-box {
          flex: 1;
          min-width: 200px;
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 9px 14px;
          background: var(--bg-surface);
          border: 1px solid var(--line-strong);
          border-radius: var(--r-sm);
          box-shadow: var(--sh-sm);
        }
        .search-box input {
          flex: 1;
          border: none;
          background: transparent;
          outline: none;
          font-size: 14px;
          color: var(--ink);
        }
        .search-box input::placeholder { color: var(--ink-muted); }
        .search-box svg { color: var(--ink-muted); }

        .inline-select {
          padding: 9px 12px;
          border: 1px solid var(--line-strong);
          border-radius: var(--r-sm);
          background: var(--bg-surface);
          box-shadow: var(--sh-sm);
          color: var(--ink);
          font-size: 14px;
          cursor: pointer;
          max-width: 180px;
          font-family: var(--iv-sans);
        }

        .btn-filter {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 9px 14px;
          background: var(--bg-surface);
          border: 1px solid var(--line-strong);
          border-radius: var(--r-sm);
          box-shadow: var(--sh-sm);
          cursor: pointer;
          font-size: 14px;
          font-weight: 500;
          color: var(--ink);
          font-family: var(--iv-sans);
        }
        .btn-filter:hover { background: var(--bg-inset); }
        .btn-filter.active { border-color: var(--accent); color: var(--accent-ink); }

        .filter-count {
          background: var(--accent);
          color: #fff;
          font-size: 11px;
          padding: 1px 6px;
          border-radius: var(--r-pill);
          min-width: 18px;
          text-align: center;
        }

        .btn-icon-only {
          padding: 9px;
          background: var(--bg-surface);
          border: 1px solid var(--line-strong);
          border-radius: var(--r-sm);
          box-shadow: var(--sh-sm);
          cursor: pointer;
          color: var(--ink-secondary);
          display: inline-flex;
          align-items: center;
          justify-content: center;
        }
        .btn-icon-only:hover { background: var(--bg-inset); color: var(--ink); }

        /* Data table */
        .tokens-table-wrap {
          border: 1px solid var(--line);
          border-radius: var(--r-lg);
          overflow: hidden;
          background: var(--surface);
          box-shadow: var(--sh-card);
        }
        .token-table { display: flex; flex-direction: column; }

        .ttable-head, .token-row {
          display: grid;
          grid-template-columns: minmax(150px, 1.5fr) 130px minmax(140px, 1.3fr) 80px 120px 110px;
          align-items: center;
          gap: var(--space-3);
          padding: 12px 16px;
        }

        .ttable-head {
          font-size: 11px;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          font-weight: 600;
          color: var(--t-500);
          background: var(--surface-2);
          border-bottom: 1px solid var(--line);
        }
        .th-dec { text-align: right; }

        .token-row {
          border-bottom: 1px solid var(--line);
          font-size: 14px;
        }
        .token-row:last-child { border-bottom: none; }
        .token-row:hover { background: var(--surface-2); }
        .token-row.is-inactive { opacity: 0.6; }

        /* Network section header within the token list — subtle band carrying
           the chain's brand color via the inline --chain-c custom property. */
        .token-group-head {
          display: flex;
          align-items: center;
          gap: 7px;
          padding: 8px 16px;
          background: var(--surface-2);
          border-bottom: 1px solid var(--line);
          border-left: 3px solid var(--chain-c, var(--line-strong));
          font-size: 11px;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          color: var(--t-500);
        }
        .token-group-head .tgh-name { white-space: nowrap; color: var(--chain-c, var(--t-700)); }

        .td-token { display: flex; align-items: center; gap: 10px; min-width: 0; }
        .token-id { display: flex; flex-direction: column; min-width: 0; }
        .t-symbol { font-weight: 700; color: var(--ink); }
        .t-name {
          font-size: 12.5px; color: var(--t-500);
          white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
        }

        .td-chain {
          display: flex; align-items: center; gap: 6px;
          font-size: 13px; color: var(--t-700);
          min-width: 0;
        }
        .td-chain span { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .chain-icon { color: var(--t-500); flex-shrink: 0; }

        .td-contract { min-width: 0; }
        .contract-cell { display: flex; align-items: center; gap: 6px; }
        .contract-cell code {
          font-family: var(--font-mono);
          font-size: 13px;
          color: var(--t-700);
        }
        .native-asset { font-size: 13px; color: var(--t-500); font-style: italic; }
        .muted { color: var(--t-500); }
        .icon-mini {
          display: inline-flex; align-items: center; justify-content: center;
          width: 24px; height: 24px;
          border: none; background: transparent; cursor: pointer;
          color: var(--t-500); border-radius: var(--r-sm);
        }
        .icon-mini:hover { background: var(--bg-inset); color: var(--ink); }

        .td-dec {
          text-align: right;
          font-family: var(--font-mono);
          color: var(--t-700);
        }

        .td-status { display: flex; align-items: center; gap: 6px; flex-wrap: wrap; }

        .td-actions {
          display: flex; align-items: center; justify-content: flex-end; gap: 2px;
          opacity: 0;
          transition: opacity 0.12s;
        }
        .token-row:hover .td-actions,
        .td-actions:focus-within { opacity: 1; }
        .row-action {
          display: inline-flex; align-items: center; justify-content: center;
          width: 32px; height: 32px;
          border: none; background: transparent; cursor: pointer;
          color: var(--t-500); border-radius: var(--r-sm);
        }
        .row-action:hover { background: var(--bg-inset); color: var(--ink); }
        .row-action.danger:hover { background: var(--err-soft); color: var(--err); }
        .row-action .text-success { color: var(--ok); }
        /* Merchant toggle switch is the primary control: keep it always visible. */
        .td-actions:has(.rd-switch) { opacity: 1; }

        /* Pagination footer */
        .pagination-bar {
          display: flex;
          align-items: center;
          justify-content: space-between;
          flex-wrap: wrap;
          gap: 12px;
          margin-top: 16px;
          font-size: 13px;
          color: var(--ink-muted);
        }
        .pagination-controls { display: flex; align-items: center; gap: 4px; }
        .page-btn {
          display: inline-flex; align-items: center; justify-content: center;
          width: 34px; height: 34px;
          border: 1px solid var(--line-strong);
          background: var(--bg-surface);
          color: var(--ink);
          border-radius: var(--r-sm);
          cursor: pointer;
        }
        .page-btn:hover:not(:disabled) { background: var(--bg-inset); }
        .page-btn:disabled { opacity: 0.4; cursor: default; }
        .page-indicator { padding: 0 10px; color: var(--ink); font-weight: 600; }

        @media (max-width: 991px) {
          .ttable-head { display: none; }
          .token-row {
            grid-template-columns: 1fr auto;
            grid-template-areas: "token actions" "chain status" "contract contract";
            row-gap: 8px;
            padding: 12px 14px;
          }
          .td-token { grid-area: token; }
          .td-actions { grid-area: actions; opacity: 1; }
          .td-chain { grid-area: chain; }
          .td-contract { grid-area: contract; }
          .td-status { grid-area: status; justify-content: flex-end; align-self: center; }
          .td-dec { display: none; }
          .row-action { width: 44px; height: 44px; }
          .icon-mini { width: 36px; height: 36px; }
          .inline-select { flex: 1; max-width: none; min-height: 44px; }
          .page-btn { width: 44px; height: 44px; }
        }

        /* Empty / loading states */
        .nt-state {
          background: var(--bg-surface);
          border: 1px solid var(--line);
          border-radius: var(--r-lg);
        }
        .nt-state .spinning { color: var(--ink-muted); margin-bottom: 12px; }
        .nt-empty-btn { margin: 16px auto 0; }

        /* Premium upsell banner */
        .nt-upsell {
          display: flex;
          align-items: center;
          gap: 16px;
          padding: 18px 20px;
          margin-top: 16px;
        }
        .nt-upsell-icon {
          width: 44px;
          height: 44px;
          border-radius: var(--r-md);
          display: grid;
          place-items: center;
          background: var(--accent-soft);
          color: var(--accent);
          flex-shrink: 0;
        }
        .nt-upsell-copy { flex: 1; min-width: 0; }
        .nt-upsell-copy h3 {
          margin: 0 0 2px;
          font-size: 14px;
          font-weight: 700;
          color: var(--ink);
        }
        .nt-upsell-copy p {
          margin: 0;
          font-size: 13px;
          color: var(--ink-secondary);
        }
        .nt-upsell-btn { flex-shrink: 0; text-decoration: none; }

        .spinning { animation: spin 1s linear infinite; }
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        .text-success { color: var(--ok) !important; }

        /* Shared button primitives used by modals */
        .btn-primary {
          display: flex;
          align-items: center;
          gap: 8px;
          background: var(--primary);
          color: #fff;
          border: none;
          padding: 10px 16px;
          border-radius: var(--r-sm);
          cursor: pointer;
          font-weight: 600;
          font-size: 14px;
        }
        .btn-primary:hover { background: var(--primary-hover); }
        .btn-primary:disabled { opacity: 0.5; cursor: not-allowed; }

        .btn-secondary {
          display: flex;
          align-items: center;
          gap: 8px;
          background: var(--bg-surface);
          color: var(--ink);
          border: 1px solid var(--line-strong);
          box-shadow: var(--sh-sm);
          padding: 10px 16px;
          border-radius: var(--r-sm);
          cursor: pointer;
          font-weight: 600;
          font-size: 14px;
        }
        .btn-secondary:hover { background: var(--bg-inset); }

        .btn-danger {
          background: var(--err);
          color: #fff;
          border: none;
          padding: 10px 16px;
          border-radius: var(--r-sm);
          cursor: pointer;
          font-weight: 600;
          font-size: 14px;
        }
        .btn-danger:hover { filter: brightness(0.93); }

        /* Modal Styles */
        .modal-overlay {
          position: fixed;
          inset: 0;
          background: rgba(24, 24, 27, 0.45);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
          padding: 16px;
        }
        .modal-container {
          background: var(--bg-surface);
          border: 1px solid var(--line);
          border-radius: var(--r-lg);
          width: 100%;
          max-width: 500px;
          max-height: 90vh;
          overflow-y: auto;
          box-shadow: var(--sh-pop);
        }
        .modal-small { max-width: 400px; }

        .modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 18px 20px;
          border-bottom: 1px solid var(--line);
        }
        .modal-header h3 {
          margin: 0;
          font-size: 16px;
          font-weight: 700;
          letter-spacing: -0.01em;
          color: var(--ink);
        }
        .modal-close {
          background: none;
          border: none;
          color: var(--ink-secondary);
          cursor: pointer;
          padding: 6px;
          border-radius: var(--r-sm);
        }
        .modal-close:hover { background: var(--bg-inset); color: var(--ink); }

        .modal-body { padding: 20px; }
        .modal-body p { color: var(--ink-secondary); font-size: 14px; }

        .modal-footer {
          display: flex;
          justify-content: flex-end;
          gap: 10px;
          padding: 14px 20px;
          border-top: 1px solid var(--line);
          background: var(--bg-subtle);
          border-radius: 0 0 var(--r-lg) var(--r-lg);
        }

        .form-group { margin-bottom: 16px; }
        .form-group label {
          display: block;
          font-size: 13px;
          font-weight: 600;
          color: var(--ink);
          margin-bottom: 7px;
        }
        .form-group input,
        .form-group select {
          width: 100%;
          padding: 9px 12px;
          border: 1px solid var(--line-strong);
          border-radius: var(--r-sm);
          font-size: 14px;
          background: var(--bg-surface);
          color: var(--ink);
          transition: border-color 0.15s, box-shadow 0.15s;
        }
        .form-group input:focus,
        .form-group select:focus {
          outline: none;
          border-color: var(--accent);
          box-shadow: 0 0 0 3px var(--accent-soft);
        }
        .form-group input:disabled,
        .form-group select:disabled {
          background: var(--bg-inset);
          cursor: not-allowed;
        }
        .input-error { border-color: var(--err) !important; }
        .error-text {
          display: flex;
          align-items: center;
          gap: 4px;
          color: var(--err);
          font-size: 12px;
          margin-top: 5px;
        }
        .form-hint {
          display: block;
          font-size: 12px;
          color: var(--ink-muted);
          margin-top: 5px;
        }
        .form-hint.warning { color: var(--warn); }
        .token-resolved {
          display: flex;
          align-items: center;
          gap: 6px;
          font-size: 12px;
          color: var(--ok, #16a34a);
          margin-top: 6px;
        }

        .form-row {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 14px;
        }
        @media (max-width: 600px) {
          .form-row { grid-template-columns: 1fr; }
        }

        .checkbox-label {
          display: flex !important;
          align-items: center;
          gap: 8px;
          cursor: pointer;
        }
        .checkbox-label input[type="checkbox"] {
          width: auto;
          cursor: pointer;
        }
      `}</style>
    </div>
  );
};

export default Tokens;
